import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { NOTIFICATIONS } from "~/constans/notifications";
import { IAssignmentItem, UsersService } from "~/entity/Users/api/service";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { defaultErrorHandlerFunc } from "~/shared/hooks/useRequestHandler";

export interface IAssignmentSaveParams {
  userId: number;
  assignments: IAssignmentItem[];
}

export default function useTemporaryAssignmentsSave() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const save = async ({ userId, assignments }: IAssignmentSaveParams) => {
    try {
      const res = await UsersService.PUT.updateTemporaryAssignments({
        userId,
        assignments,
      });

      // Бэк может вернуть 200 с частичными ошибками (success: false /
      // results.errors не пуст) — это не успех: показываем детали и бросаем,
      // чтобы catch в UsersForm не закрыл модалку.
      const data = res?.data;
      const resultErrors = data?.results?.errors?.filter(Boolean) ?? [];
      if (!data || data.success === false || resultErrors.length > 0) {
        // Список ошибок по дилерам — построчно маркированным списком
        // (Notification рендерит content с white-space: pre-line).
        const details = resultErrors.map((err) => `• ${err}`).join("\n");
        addNotificationMutation({
          variables: {
            type: NOTIFICATIONS.error,
            content: `${data?.message ?? t("users.assignments_save_failed")}${
              details ? `:\n${details}` : ""
            }`,
            duration: 15000,
          },
        });
        throw new Error("assignments save failed");
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        defaultErrorHandlerFunc({
          e,
          t,
          addNotificationMutation,
          errorPrefixMessage: t("users.assignments_save_failed"),
          duration: 15000,
        });
      }
      throw new Error("assignments save failed");
    }

    queryClient.invalidateQueries(["getUserAssignments"]);
    queryClient.invalidateQueries(["getUsers"]);
    queryClient.invalidateQueries(["allTemporaryAssignments"]);
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.success,
        content: t("users.assignments_save_success"),
      },
    });
  };

  return { mutateAsync: save };
}
